/**
 * The password rules, mirrored from `apps/api/src/common/password.ts` so the browser can show a
 * live checklist while the user types instead of refusing after the round trip.
 *
 * The API copy is the one that DECIDES — this one only explains. Keep them in step: the
 * executable cases live in `apps/api/test/password-rules.test.mjs` (apps/web has no test runner).
 */

export const PASSWORD_MIN_LENGTH = 8;

export type PasswordRuleId =
  | 'minLength'
  | 'letterAndDigit'
  | 'noSequential'
  | 'noRepeated'
  | 'notCurrent'
  | 'match';

/** Display order of the checklist, and of the API's failure list for the four shared rules. */
export const PASSWORD_RULE_ORDER: readonly PasswordRuleId[] = [
  'minLength',
  'letterAndDigit',
  'noSequential',
  'noRepeated',
  'notCurrent',
  'match',
];

/** Three or more identical characters in a row ('aaa', '111'). */
function hasRepeatedRun(password: string): boolean {
  for (let i = 2; i < password.length; i++) {
    if (password[i] === password[i - 1] && password[i - 1] === password[i - 2]) return true;
  }
  return false;
}

/** Three or more characters running up or down by one codepoint ('abc', '321'). */
function hasSequentialRun(password: string): boolean {
  for (let i = 2; i < password.length; i++) {
    const d1 = password.charCodeAt(i - 1) - password.charCodeAt(i - 2);
    const d2 = password.charCodeAt(i) - password.charCodeAt(i - 1);
    if (d1 === d2 && Math.abs(d1) === 1) return true;
  }
  return false;
}

/**
 * Which rules the typed password satisfies.
 *
 * A field the FORM does not have (the recovery screens have no current password) is passed as
 * `undefined` and its rule passes — it is not the user's to satisfy. A field the form HAS but
 * the user has not filled in yet stays grey: an empty confirmation ticking "as duas senhas
 * coincidem" green would be a lie.
 */
export function checkPassword({
  next,
  confirm,
  current,
}: {
  next: string;
  confirm?: string;
  current?: string;
}): Record<PasswordRuleId, boolean> {
  return {
    minLength: next.length >= PASSWORD_MIN_LENGTH,
    letterAndDigit: /[a-zA-Z]/.test(next) && /[0-9]/.test(next),
    noSequential: !hasSequentialRun(next),
    noRepeated: !hasRepeatedRun(next),
    notCurrent: current === undefined || (current.length > 0 && next !== current),
    match: confirm === undefined || (confirm.length > 0 && next === confirm),
  };
}

/** Every rule green. Gate the submit button on this. */
export function isPasswordValid(input: {
  next: string;
  confirm?: string;
  current?: string;
}): boolean {
  return Object.values(checkPassword(input)).every(Boolean);
}

/** Which rules to show for a form that has no current password to compare against. */
export const RESET_RULE_IDS: readonly PasswordRuleId[] = PASSWORD_RULE_ORDER.filter(
  (id) => id !== 'notCurrent',
);
